import ArrowLink from "@/components/ui/arrow-link";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/lib/utils";
import Image, { StaticImageData } from "next/image";
import { memo } from "react";

export type SupportCardProps = {
  title: string;
  description: string;
  imageUrl: string | StaticImageData;
  href?: string;
  linkLabel?: string;
  imageRatioBoost?: boolean;
  responsive?: boolean;
} & React.ComponentProps<"div">;

const SupportCard = ({
  title,
  imageUrl,
  description,
  className,
  href = "#",
  linkLabel,
  imageRatioBoost = false,
  responsive = true,
}: SupportCardProps) => {
  return (
    <div
      className={cn(
        "flex flex-col border bg-white border-neutral-200 rounded-3xl overflow-hidden",
        { "lg:border-0 lg:rounded-2xl": responsive },
        className
      )}
    >
      <div
        className={cn(
          "flex relative overflow-hidden shrink-0 aspect-[1/0.56]",
          {
            "lg:aspect-[1/0.63]": imageRatioBoost,
            "lg:mt-5 lg:mx-5 lg:rounded-[8px] lg:aspect-[1/0.44]": responsive,
            "rounded-b-3xl": !responsive,
          }
        )}
      >
        <Image
          alt={title}
          src={imageUrl}
          fill
          className="w-full h-full flex-auto min-w-0"
          objectFit="cover"
          loading="lazy"
        />
      </div>
      <div className="flex flex-col p-5 min-w-0 flex-auto lg:pt-3">
        <div className="grid grid-cols-1 gap-4 lg:gap-3 place-content-start flex-auto">
          <Typography
            variant="titleMd"
            asChild
            className="font-medium text-semantic-text-primary lg:font-semibold lg:leading-[150%] lg:text-gray-800"
          >
            <h4>{title}</h4>
          </Typography>
          {!!description && (
            <Typography
              variant="labelFixedMd"
              asChild
              className={cn("text-semantic-text-primary leading-[160%]", {
                "lg:text-sm lg:leading-[150%] lg:text-gray-600": responsive,
              })}
            >
              <p>{description}</p>
            </Typography>
          )}
        </div>
        {!!linkLabel && (
          <div className="mt-auto pt-[23] pb-[11] sm:pt-3 sm:pb-0 flex">
            <ArrowLink href={href}>{linkLabel}</ArrowLink>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(SupportCard);
